/**
 * Flex Pipe Tool
 * 
 * Spline-based flexible pipe placement.
 */

import { v4 as uuidv4 } from 'uuid';
import { FlexPipeElement, FlexPipeGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export class FlexPipeTool extends BaseTool {
    id = 'flex_pipe';
    name = 'Flex Pipe';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.points.push(snapped);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 2) {
                this.finishPath();
            }
        } else if (event.key === 'Escape') {
            if (this.points.length > 0) {
                this.points = [];
                this.updatePreview();
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.points.length > 0 && this.currentPoint) {
            const previewPoints = [...this.points, this.currentPoint];
            this.context.appStore.setToolPreview({
                type: 'flex_pipe',
                points: previewPoints,
                diameter: 0.05
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private finishPath() {
        if (!this.context) return;

        const controlPoints: [number, number, number][] = this.points.map(p => [p.x, p.y, p.z]);

        // Calculate accurate spline length
        const curve = new THREE.CatmullRomCurve3(this.points);
        const length = curve.getLength();

        const geometryData: FlexPipeGeometryData = {
            controlPoints,
            diameter: 50 // 50mm default
        };

        const flexPipe: FlexPipeElement = {
            id: uuidv4(),
            category: 'Flex Pipes',
            type: 'Flexible Pipe',
            parameters: {
                'Diameter': '50 mm',
                'Length': length.toFixed(2) + ' m',
                'System Type': 'Domestic Hot Water'
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(flexPipe);

        this.points = [];
        this.context.appStore.setToolPreview(null);
    }
}
